import { useState } from "react";
import { Link } from "react-router-dom";
import { useRecentAdminActivity } from "../../hooks/useAdminActivity";
import TablePagination from "../common/TablePagination";
import type {
  AdminActivityAction,
  AdminActivityEntityType,
} from "../../api/adminActivity";

const actionLabels: Record<AdminActivityAction, string> = {
  created: "Created",
  updated: "Updated",
  deactivated: "Deactivated",
};

const entityLabels: Record<AdminActivityEntityType, string> = {
  lender: "Lender",
  user: "User",
  "los-integration": "LOS Integration",
  "intelligent-ordering": "Intelligent Ordering",
};

function formatAction(
  action: AdminActivityAction,
  entity: AdminActivityEntityType
): string {
  return `${entityLabels[entity]} ${actionLabels[action]}`;
}

const PAGE_SIZE = 20;

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace(",", " -");
}

export default function RecentActivity() {
  const [pageIndex, setPageIndex] = useState(0);
  const { data, isLoading, isFetching, isError } = useRecentAdminActivity({
    offset: pageIndex * PAGE_SIZE,
    limit: PAGE_SIZE,
  });
  const activities = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <section aria-labelledby="recent-activity-heading">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h2
          id="recent-activity-heading"
          className="text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          Recent Activity
        </h2>
        {/* TODO: Implement the dedicated /activity-log page for the complete activity history. */}
        <Link
          to="/activity-log"
          className="rounded px-3 py-1.5 text-xs font-normal text-brand transition-colors hover:bg-brand-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
        >
          View all
        </Link>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        {isLoading || isFetching ? (
          <div
            role="status"
            className="flex items-center justify-center gap-2 p-8 text-sm text-gray-500 dark:text-gray-400"
          >
            <span
              className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
              aria-hidden="true"
            />
            Loading recent activity...
          </div>
        ) : isError ? (
          <p
            role="alert"
            className="p-8 text-center text-sm text-red-600 dark:text-red-400"
          >
            Unable to load recent activity.
          </p>
        ) : activities.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No recent activity
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-head text-left text-white">
                  <th scope="col" className="px-3 py-2.5 text-xs font-semibold">
                    User
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-xs font-semibold">
                    Action
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-xs font-semibold">
                    Lender
                  </th>
                  <th
                    scope="col"
                    className="whitespace-nowrap px-3 py-2.5 text-xs font-semibold"
                  >
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {activities.map((activity) => {
                  const lenders = activity.lenders
                    .map((lender) => lender.displayName ?? lender.id)
                    .join(", ");
                  const target =
                    activity.entity.displayName?.trim() || activity.entity.id;

                  return (
                    <tr key={activity.uuid}>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs font-normal text-content">
                        {activity.actor.displayName}
                      </td>
                      <td className="max-w-xs px-3 py-2.5 text-xs text-content-secondary">
                        <div className="whitespace-nowrap">
                          {formatAction(activity.action, activity.entity.type)}
                        </div>
                        <div className="mt-1 whitespace-normal [overflow-wrap:anywhere] text-content-secondary">
                          {target}
                        </div>
                      </td>
                      <td
                        className="max-w-xs truncate px-3 py-2.5 text-xs text-content-secondary"
                        title={lenders || undefined}
                      >
                        {lenders || "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-content-secondary">
                        {formatTimestamp(activity.occurredAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <TablePagination
          totalRows={total}
          pageIndex={pageIndex}
          pageSize={PAGE_SIZE}
          onPageChange={setPageIndex}
          disabled={isFetching}
        />
      )}
    </section>
  );
}
